const REQUEST_ID_PATTERN =
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const STEPS = new Set([
    'content_bridge',
    'device_check',
    'enrollment_fetch',
    'header_refresh',
    'status_storage',
    'reply_delivery',
    'page_received',
])
const PHASES = new Set(['started', 'completed', 'failed'])
const BATCH_SIZE = 4
const QUEUE_LIMIT = 60
const FLUSH_INTERVAL_MS = 3000

const nonnegativeInteger = value =>
    Number.isSafeInteger(value) && value >= 0 ? value : undefined
const code = value =>
    typeof value === 'string' && /^[a-zA-Z_]{1,40}$/.test(value)
        ? value
        : undefined
const boolean = value => (typeof value === 'boolean' ? value : undefined)

export const pickEnrollmentCheckpoint = value => {
    if (
        !value ||
        typeof value !== 'object' ||
        typeof value.requestId !== 'string' ||
        !REQUEST_ID_PATTERN.test(value.requestId) ||
        !STEPS.has(value.step) ||
        !PHASES.has(value.phase) ||
        nonnegativeInteger(value.atMs) === undefined
    )
        return null
    return {
        requestId: value.requestId,
        step: value.step,
        phase: value.phase,
        atMs: value.atMs,
        durationMs: nonnegativeInteger(value.durationMs),
        outcome: code(value.outcome),
        httpStatus: nonnegativeInteger(value.httpStatus),
        enrollmentState:
            value.enrollmentState === null ? null : code(value.enrollmentState),
        hasEnrollmentId: boolean(value.hasEnrollmentId),
        allowEnrollmentRefresh: boolean(value.allowEnrollmentRefresh),
        isMismatched: boolean(value.isMismatched),
        isSnoozed: boolean(value.isSnoozed),
        source: code(value.source),
        cooldownMs: nonnegativeInteger(value.cooldownMs),
        failureCount: nonnegativeInteger(value.failureCount),
    }
}

// Batch checkpoints independently of React renders and final enrollment replies.
// Keep batches small enough for the existing 4 KiB endpoint limit.
export const createEnrollmentCheckpointBatcher = onBatch => {
    const queue = []
    let timer = null
    let stopped = false
    let dropped = 0

    const sendBatch = () => {
        if (!queue.length) return
        const batch = {
            extensionCheckpoints: queue.splice(0, BATCH_SIZE),
            droppedCheckpoints: dropped,
        }
        dropped = 0
        try {
            Promise.resolve(onBatch(batch)).catch(() => {})
        } catch {
            /* Diagnostic transport cannot affect assessment state. */
        }
    }

    const schedule = () => {
        if (timer !== null || stopped || !queue.length) return
        timer = setTimeout(() => {
            timer = null
            sendBatch()
            schedule()
        }, FLUSH_INTERVAL_MS)
    }

    return {
        add: value => {
            if (stopped) return
            const checkpoint = pickEnrollmentCheckpoint(value)
            if (!checkpoint) return
            if (queue.length === QUEUE_LIMIT) {
                queue.shift()
                dropped += 1
            }
            queue.push(checkpoint)
            schedule()
        },
        dispose: () => {
            stopped = true
            clearTimeout(timer)
            // Preserve completed checks when the student advances before the next flush.
            while (queue.length) sendBatch()
        },
    }
}
